import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Alert,
  Textarea,
  Divider,
  Select,
  Option,
  Input,
  FormControl,
  FormLabel,
} from '@mui/joy';
import { Plus, FloppyDisk as Save, Sparkle, X } from '@phosphor-icons/react';
import { FeatureGate } from '../../components/common/FeatureGate';
import {
  AddressSearch,
  JobSelection,
  OpportunitySelection,
  MeasurementsList
} from '../../components/measurements';
import { SimpleGoogleMap } from '../../components/measurements/SimpleGoogleMap';
import { DrawableGoogleMap } from '../../components/measurements/DrawableGoogleMap';
import { LineSegmentsPanel } from '../../components/measurements/LineSegmentsPanel';
import { LinearTotalsPanel } from '../../components/measurements/LinearTotalsPanel';
import { CardinalViewsPanel } from '../../components/measurements/CardinalViewsPanel';
import {
  LINE_TYPES,
  PITCH_MULTIPLIERS,
  PITCH_OPTIONS,
  DEFAULT_PITCH,
  DEFAULT_WASTE_FACTOR,
} from '../../components/measurements/lineDrawingConstants';
import {
  useCreateMeasurement,
  useUpdateMeasurement,
  useMeasurement,
  useCreateRoofSection,
  useUpdateRoofSection,
  useDeleteRoofSection,
  useRoofSections
} from '../../hooks/measurements';
import {
  useRoofFeatures,
  useCreateRoofFeature,
  useUpdateRoofFeature,
  useDeleteRoofFeature,
  useSyncLinearTotals,
  useGenerateReportFromMeasurement,
} from '../../hooks/measurements/useRoofFeatures';
import {
  useStartAutoDraw,
  useAutoDrawStatus,
  useAcceptAutoDraw,
  useRejectAutoDraw,
  type AutoDrawConflict,
} from '../../hooks/measurements/useAutoDraw';
import { AutoDrawReviewBanner } from '../../components/measurements/AutoDrawReviewBanner';
import { apiClient } from '../../lib/api';
import { geocodeAddressLine } from '../../utils/loadGoogleMaps';
import {
  applyPrefill,
  formatAddressLine,
  parseAddressLine,
  recordAddressFields,
} from '../../utils/prefill';
import { useDetailedMeasurements } from '../../hooks/measurements/useDetailedMeasurements';
import type {
  Measurement,
  MeasurementFormData,
  GoogleMapsPlace,
  Job,
  Opportunity,
  RoofSection,
  RoofFeature
} from '../../types';

const MeasurementsPageContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState<Measurement | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [formData, setFormData] = useState<Partial<MeasurementFormData>>({
    notes: ''
  });
  
  // Phase 2: Advanced drawing mode state
  const [showAdvancedMode, setShowAdvancedMode] = useState(false);
  const [currentMeasurementId, setCurrentMeasurementId] = useState<number | null>(null);

  const createMutation = useCreateMeasurement();
  const updateMutation = useUpdateMeasurement();
  
  // Phase 2: Roof section hooks
  const createRoofSectionMutation = useCreateRoofSection();
  const updateRoofSectionMutation = useUpdateRoofSection();
  const deleteRoofSectionMutation = useDeleteRoofSection();
  
  // Fetch roof sections for current measurement
  const { data: roofSections = [], refetch: refetchRoofSections } = useRoofSections(currentMeasurementId || undefined);

  // Roof feature hooks for line drawing
  const { data: roofFeatures = [], refetch: refetchRoofFeatures } = useRoofFeatures(currentMeasurementId || undefined);
  const createRoofFeatureMutation = useCreateRoofFeature();
  const updateRoofFeatureMutation = useUpdateRoofFeature();
  const deleteRoofFeatureMutation = useDeleteRoofFeature();
  const syncLinearTotalsMutation = useSyncLinearTotals();
  const generateReportMutation = useGenerateReportFromMeasurement();

  // Auto-draw (AI roof detection)
  const startAutoDrawMutation = useStartAutoDraw();
  const acceptAutoDrawMutation = useAcceptAutoDraw();
  const rejectAutoDrawMutation = useRejectAutoDraw();
  const { data: autoDrawRun, refetch: refetchAutoDrawStatus } = useAutoDrawStatus(
    currentMeasurementId || undefined,
    showAdvancedMode
  );
  const autoDrawRunning =
    startAutoDrawMutation.isPending ||
    autoDrawRun?.status === 'queued' ||
    autoDrawRun?.status === 'processing';
  const autoDrawPendingReview = autoDrawRun?.status === 'completed';
  // Group nudge (Shift+arrows) to fix imagery-registration offset in review
  const [autoOffset, setAutoOffset] = useState({ lat: 0, lng: 0 });
  const [visibleLineTypes, setVisibleLineTypes] = useState<Set<string>>(new Set(LINE_TYPES));
  const [highlightedFeatureId, setHighlightedFeatureId] = useState<number | null>(null);
  const [selectedPitch, setSelectedPitch] = useState<string>(DEFAULT_PITCH);
  const [wasteFactor, setWasteFactor] = useState<number>(DEFAULT_WASTE_FACTOR);

  // Load saved pitch & waste factor from DetailedMeasurement when editing
  const { data: detailedMeasurements } = useDetailedMeasurements(currentMeasurementId || 0);
  useEffect(() => {
    if (detailedMeasurements?.length) {
      const dm = detailedMeasurements[0];
      if (dm.predominant_pitch) setSelectedPitch(dm.predominant_pitch);
      if (dm.waste_factor != null) setWasteFactor(dm.waste_factor);
    }
  }, [detailedMeasurements]);

  // Handle edit navigation from detail page or URL query param.
  //
  // `?measurementId=` is read once into state: the opportunityId effect below
  // clears the query string straight away, and reading the param on every
  // render would drop the edit target before useMeasurement has loaded it,
  // leaving a blank create form whose save 409s.
  const [pendingEditId, setPendingEditId] = useState<number>(
    () => parseInt(searchParams.get('measurementId') || '', 10) || 0,
  );
  const editMeasurementId = location.state?.editMeasurementId || pendingEditId;
  const { data: measurementToEdit } = useMeasurement(editMeasurementId || 0);

  useEffect(() => {
    if (measurementToEdit && editMeasurementId) {
      handleStartEdit(measurementToEdit);
      setPendingEditId(0);
      // Clear the state/search params to prevent re-triggering
      navigate('.', { replace: true, state: {} });
    }
  }, [measurementToEdit, editMeasurementId]);

  // Handle opportunityId / jobId query params from URL.
  //
  // "Measure" on a Job or Opportunity used to pass the id and nothing else, so
  // the rep had to re-run a Google Places search for an address the source
  // record already holds. Seed the address and coordinates too: an Opportunity
  // carries geocoded lat/lng, and a Job's address is geocoded once here.
  const opportunityIdParam = searchParams.get('opportunityId');
  const jobIdParam = searchParams.get('jobId');
  const replaceExistingParam = searchParams.get('replaceExisting') === 'true';

  useEffect(() => {
    if (!opportunityIdParam && !jobIdParam) return;
    // Editing an existing measurement: the edit effect above loads it, opportunity
    // included, so there is no create form to seed.
    if (pendingEditId) {
      setSearchParams({}, { replace: true });
      return;
    }

    const isOpportunity = Boolean(opportunityIdParam);
    const sourceId = parseInt((opportunityIdParam || jobIdParam) as string);
    if (Number.isNaN(sourceId)) return;

    let cancelled = false;

    setShowCreateForm(true);
    setFormData(prev => ({
      ...prev,
      ...(isOpportunity ? { opportunity: sourceId } : { job: sourceId }),
      ...(replaceExistingParam ? { replace_existing: true } : {}),
    }));
    // Clear the query param to prevent re-triggering
    setSearchParams({}, { replace: true });

    const seedFromSource = async () => {
      try {
        // If navigating for an opportunity without replaceExisting, check if an
        // ApexOS measurement already exists on this opportunity and open it in edit mode.
        if (isOpportunity && !replaceExistingParam) {
          const existingRes = await apiClient.get<{ results?: Measurement[] } | Measurement[]>(
            `/api/measurements/?opportunity=${sourceId}`,
          );
          if (cancelled) return;
          const existingList = Array.isArray(existingRes.data)
            ? existingRes.data
            : existingRes.data?.results || [];
          if (existingList.length > 0) {
            handleStartEdit(existingList[0]);
            return;
          }
        }

        const path = isOpportunity
          ? `/api/crm/opportunities/${sourceId}/`
          : `/api/jobs/${sourceId}/`;
        const response = await apiClient.get<Record<string, unknown>>(path);
        if (cancelled) return;

        const record = response.data;
        const address = recordAddressFields(record);
        if (!address) return;

        const line = formatAddressLine(address);
        let lat = record.latitude != null ? Number(record.latitude) : NaN;
        let lng = record.longitude != null ? Number(record.longitude) : NaN;

        // A Job has no stored coordinates, and an Opportunity's may not have
        // been geocoded yet. Resolve them from the address so the rep still
        // does not have to search for it.
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          const geocoded = await geocodeAddressLine(line);
          if (cancelled || !geocoded) return;
          lat = geocoded.lat;
          lng = geocoded.lng;
        }

        if (cancelled) return;
        setSelectedAddress(line);
        setSelectedLocation({ lat, lng });
        setFormData(prev =>
          applyPrefill(prev, {
            address: line,
            latitude: parseFloat(lat.toFixed(8)),
            longitude: parseFloat(lng.toFixed(8)),
          }),
        );
      } catch (error) {
        // A failed seed is not fatal: the address search still works, the rep
        // just types it as they did before.
        console.warn('Could not seed the measurement address from its source record', error);
      }
    };

    seedFromSource();
    return () => {
      cancelled = true;
    };
  }, [opportunityIdParam, jobIdParam, replaceExistingParam, pendingEditId, setSearchParams]);

  const handleAddressSelect = (place: GoogleMapsPlace) => {
    console.log('MeasurementsPage: Address selected:', place);
    setSelectedAddress(place.formatted_address);
    const newLocation = {
      lat: place.geometry.location.lat,
      lng: place.geometry.location.lng
    };
    console.log('MeasurementsPage: Setting selectedLocation to:', newLocation);
    setSelectedLocation(newLocation);
    setFormData(prev => ({
      ...prev,
      address: place.formatted_address,
      latitude: parseFloat(place.geometry.location.lat.toFixed(8)),
      longitude: parseFloat(place.geometry.location.lng.toFixed(8))
    }));
  };

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setSelectedAddress(location.address);
    setSelectedLocation({ lat: location.lat, lng: location.lng });
    setFormData(prev => ({
      ...prev,
      address: location.address,
      latitude: parseFloat(location.lat.toFixed(8)),
      longitude: parseFloat(location.lng.toFixed(8))
    }));
  };

  const handleJobSelect = (job: Job | null) => {
    setSelectedJob(job);
    setFormData(prev => ({
      ...prev,
      job: job?.id
    }));
  };

  const handleOpportunitySelect = (opportunity: Opportunity | null) => {
    setSelectedOpportunity(opportunity);
    setFormData(prev => ({
      ...prev,
      opportunity: opportunity?.id
    }));
  };

  const handleStartCreate = () => {
    setShowCreateForm(true);
    setEditingMeasurement(null);
    setSelectedJob(null);
    setSelectedOpportunity(null);
    setSelectedAddress('');
    setSelectedLocation(null);
    setFormData({
      notes: ''
    });
  };

  const handleStartEdit = (measurement: Measurement) => {
    setEditingMeasurement(measurement);
    setShowCreateForm(true);
    setSelectedAddress(measurement.address);
    setSelectedLocation({
      lat: Number(measurement.latitude),
      lng: Number(measurement.longitude)
    });
    setFormData({
      opportunity: measurement.opportunity,
      job: measurement.job,
      address: measurement.address,
      latitude: parseFloat(Number(measurement.latitude).toFixed(8)),
      longitude: parseFloat(Number(measurement.longitude).toFixed(8)),
      notes: measurement.notes
    });
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingMeasurement(null);
    setSelectedJob(null);
    setSelectedOpportunity(null);
    setSelectedAddress('');
    setSelectedLocation(null);
    setFormData({
      notes: ''
    });
  };

  /**
   * Write this measurement's verified address onto its linked Opportunity if
   * that record has no address of its own. Never overwrites an address that is
   * already set, and a failure here must not fail the save - the measurement
   * itself is what the user asked for.
   */
  const backfillSourceAddress = async () => {
    const opportunityId = formData.opportunity;
    if (!opportunityId || !formData.address) return;

    try {
      const { data: opportunity } = await apiClient.get<Record<string, unknown>>(
        `/api/crm/opportunities/${opportunityId}/`,
      );
      if (recordAddressFields(opportunity)) return; // already has one

      const parsed = parseAddressLine(formData.address);
      if (!parsed) return;

      await apiClient.patch(`/api/crm/opportunities/${opportunityId}/`, {
        ...parsed,
        ...(formData.latitude != null ? { latitude: formData.latitude } : {}),
        ...(formData.longitude != null ? { longitude: formData.longitude } : {}),
      });
    } catch (error) {
      console.warn('Could not write the measurement address back to the opportunity', error);
    }
  };

  const handleSave = async (overrideReplaceExisting?: unknown) => {
    if (!formData.address || !formData.latitude || !formData.longitude) {
      alert('Please search for a property address.');
      return;
    }

    const payload: MeasurementFormData = {
      ...(formData as MeasurementFormData),
      ...(overrideReplaceExisting === true ? { replace_existing: true } : {}),
    };

    try {
      let measurementId: number;
      
      if (editingMeasurement) {
        const updatedMeasurement = await updateMutation.mutateAsync({
          id: editingMeasurement.id,
          data: payload,
        });
        measurementId = updatedMeasurement.id;
      } else {
        const newMeasurement = await createMutation.mutateAsync(payload);
        measurementId = newMeasurement.id;
      }

      // Push the Google-verified address back onto a linked Opportunity that
      // has none. The measurement is the point at which the property address
      // becomes authoritative, and without this the rep types it a third time
      // when the opportunity is worked or converted to a job.
      await backfillSourceAddress();

      // Go directly to roof drawing mode
      setCurrentMeasurementId(measurementId);
      setShowAdvancedMode(true);
      setShowCreateForm(false);
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
      const errData = response?.data;

      if (response?.status === 409 && errData?.code === 'measurement_conflict') {
        const existingId = Number(errData.existing_measurement_id);
        if (!editingMeasurement && errData.existing_type === 'apexos' && existingId) {
          const openExisting = window.confirm(
            'An ApexOS Quick Measurement already exists for this opportunity.\n\nClick OK to open and edit the existing measurement, or Cancel to choose whether to replace it.',
          );
          if (openExisting) {
            try {
              const res = await apiClient.get<Measurement>(`/api/measurements/${existingId}/`);
              handleStartEdit(res.data);
              setCurrentMeasurementId(res.data.id);
              setShowAdvancedMode(true);
              setShowCreateForm(false);
              return;
            } catch (fetchErr) {
              console.warn('Could not load existing measurement:', fetchErr);
            }
          }
        }

        const confirmReplace = window.confirm(
          `${typeof errData.detail === 'string' ? errData.detail : 'This opportunity already has a measurement.'}\n\nClick OK to replace (delete) the existing measurement on this opportunity.`,
        );
        if (confirmReplace) {
          await handleSave(true);
          return;
        }
        return;
      }

      console.error('Failed to save measurement:', error);
      alert(
        typeof errData?.detail === 'string'
          ? errData.detail
          : 'Failed to save measurement. Please try again.',
      );
    }
  };

  // Phase 2: Roof section handlers
  const handleRoofSectionCreate = async (sectionData: Omit<RoofSection, 'id' | 'created_at' | 'updated_at'>) => {
    if (!currentMeasurementId) {
      console.error('No measurement ID available');
      return;
    }
    
    console.log('Creating roof section:', sectionData);
    
    try {
      const result = await createRoofSectionMutation.mutateAsync({
        ...sectionData,
        measurement: currentMeasurementId
      });
      console.log('Roof section created successfully:', result);
      refetchRoofSections();
    } catch (error) {
      console.error('Failed to create roof section:', error);
      alert('Failed to create roof section. Please try again.');
    }
  };

  const handleRoofSectionUpdate = async (sectionId: number, updates: Partial<RoofSection>) => {
    try {
      await updateRoofSectionMutation.mutateAsync({ id: sectionId, data: updates });
      refetchRoofSections();
    } catch (error) {
      console.error('Failed to update roof section:', error);
      alert('Failed to update roof section. Please try again.');
    }
  };

  const handleRoofSectionDelete = async (sectionId: number) => {
    try {
      await deleteRoofSectionMutation.mutateAsync(sectionId);
      refetchRoofSections();
    } catch (error) {
      console.error('Failed to delete roof section:', error);
      alert('Failed to delete roof section. Please try again.');
    }
  };

  // Roof feature handlers for line drawing
  const handleFeatureCreate = useCallback(async (featureData: Omit<RoofFeature, 'id' | 'created_at' | 'updated_at'>) => {
    if (!currentMeasurementId) return;

    try {
      await createRoofFeatureMutation.mutateAsync({
        ...featureData,
        measurement: currentMeasurementId,
      });
      refetchRoofFeatures();
    } catch (error) {
      console.error('Failed to create roof feature:', error);
    }
  }, [currentMeasurementId, createRoofFeatureMutation, refetchRoofFeatures]);

  const handleFeatureUpdate = useCallback(async (featureId: number, updates: Partial<RoofFeature>) => {
    try {
      await updateRoofFeatureMutation.mutateAsync({ id: featureId, data: updates });
      refetchRoofFeatures();
    } catch (error) {
      console.error('Failed to update roof feature:', error);
    }
  }, [updateRoofFeatureMutation, refetchRoofFeatures]);

  const handleFeatureDelete = useCallback(async (featureId: number) => {
    try {
      await deleteRoofFeatureMutation.mutateAsync(featureId);
      refetchRoofFeatures();
    } catch (error) {
      console.error('Failed to delete roof feature:', error);
    }
  }, [deleteRoofFeatureMutation, refetchRoofFeatures]);

  // Prefill the pitch dropdown from the auto-draw suggestion when detection completes
  useEffect(() => {
    if (autoDrawPendingReview && autoDrawRun?.suggested_pitch) {
      setSelectedPitch(autoDrawRun.suggested_pitch);
    }
  }, [autoDrawPendingReview, autoDrawRun?.suggested_pitch]);

  useEffect(() => {
    setAutoOffset({ lat: 0, lng: 0 });
  }, [autoDrawRun?.id]);

  useEffect(() => {
    if (!autoDrawPendingReview) return;
    const STEP_M = 0.15; // ~6 inches per press
    const handler = (e: KeyboardEvent) => {
      if (!e.shiftKey || !e.key.startsWith('Arrow')) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      const mPerDegLat = 111320;
      setAutoOffset((prev) => {
        const mPerDegLng = mPerDegLat * Math.cos(((selectedLocation?.lat || 0) * Math.PI) / 180);
        const next = { ...prev };
        if (e.key === 'ArrowUp') next.lat += STEP_M / mPerDegLat;
        if (e.key === 'ArrowDown') next.lat -= STEP_M / mPerDegLat;
        if (e.key === 'ArrowRight') next.lng += STEP_M / mPerDegLng;
        if (e.key === 'ArrowLeft') next.lng -= STEP_M / mPerDegLng;
        return next;
      });
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [autoDrawPendingReview, selectedLocation?.lat]);

  const handleAutoDraw = useCallback(async (replace = false) => {
    if (!currentMeasurementId) return;
    try {
      await startAutoDrawMutation.mutateAsync({ measurementId: currentMeasurementId, replace });
      refetchAutoDrawStatus();
    } catch (error) {
      const conflict = (error as { conflict?: AutoDrawConflict }).conflict;
      if (conflict?.requires_replace) {
        if (window.confirm(
          'Auto-draw already ran for this measurement. Replace the auto-drawn geometry? '
          + 'Lines you drew manually are kept.'
        )) {
          handleAutoDraw(true);
        }
        return;
      }
      if (conflict?.run_id) {
        // A run is already in progress — just resume watching it
        refetchAutoDrawStatus();
        return;
      }
      console.error('Failed to start Auto-draw:', error);
      const message = (error as { responseData?: { error?: string } }).responseData?.error;
      alert(message || 'Failed to start Auto-draw. Please try again.');
    }
  }, [currentMeasurementId, startAutoDrawMutation, refetchAutoDrawStatus]);

  const handleAutoDrawAccept = useCallback(async () => {
    if (!currentMeasurementId || !autoDrawRun) return;
    try {
      // Persist the group nudge before accepting
      if (autoOffset.lat !== 0 || autoOffset.lng !== 0) {
        const shift = (coords: Array<{ lat: number; lng: number }>) =>
          coords.map((c) => ({ lat: c.lat + autoOffset.lat, lng: c.lng + autoOffset.lng }));
        await Promise.all([
          ...roofSections
            .filter((s) => s.source === 'auto_ai')
            .map((s) =>
              updateRoofSectionMutation.mutateAsync({
                id: s.id,
                data: { polygon_coordinates: shift(s.polygon_coordinates) },
              })
            ),
          ...roofFeatures
            .filter((f) => f.source === 'auto_ai')
            .map((f) =>
              updateRoofFeatureMutation.mutateAsync({
                id: f.id,
                data: { coordinates: shift(f.coordinates) },
              })
            ),
        ]);
        setAutoOffset({ lat: 0, lng: 0 });
      }
      await acceptAutoDrawMutation.mutateAsync({
        measurementId: currentMeasurementId,
        runId: autoDrawRun.id,
        predominantPitch: selectedPitch,
        wasteFactor,
      });
    } catch (error) {
      console.error('Failed to accept Auto-draw results:', error);
      alert('Failed to accept the auto-drawn measurements. Please try again.');
    }
  }, [currentMeasurementId, autoDrawRun, acceptAutoDrawMutation, selectedPitch, wasteFactor, autoOffset, roofSections, roofFeatures, updateRoofSectionMutation, updateRoofFeatureMutation]);

  const handleAutoDrawReject = useCallback(async () => {
    if (!currentMeasurementId || !autoDrawRun) return;
    try {
      await rejectAutoDrawMutation.mutateAsync({
        measurementId: currentMeasurementId,
        runId: autoDrawRun.id,
      });
      refetchRoofSections();
      refetchRoofFeatures();
    } catch (error) {
      console.error('Failed to discard Auto-draw results:', error);
      alert('Failed to discard the auto-drawn measurements. Please try again.');
    }
  }, [currentMeasurementId, autoDrawRun, rejectAutoDrawMutation, refetchRoofSections, refetchRoofFeatures]);

  const handleToggleLineType = useCallback((type: string) => {
    setVisibleLineTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const handleAdvancedModeExit = () => {
    setShowAdvancedMode(false);
    setCurrentMeasurementId(null);
  };

  const isFormValid = formData.address && formData.latitude && formData.longitude;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 0 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
          {showAdvancedMode ? 'Roof Drawing & Measurements' : 'Measurements'}
        </Typography>
        {!showCreateForm && !showAdvancedMode && (
          <Button
            variant="solid"
            color="primary"
            startDecorator={<Plus size={18} />}
            onClick={handleStartCreate}
          >
            New Measurement
          </Button>
        )}
        {showAdvancedMode && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Sparkle size={18} />}
              disabled
              title="AI roof detection is almost ready"
            >
              Auto-Draw Coming Soon!
            </Button>
            <Button
              variant="solid"
              color="success"
              startDecorator={<Save size={18} />}
              loading={syncLinearTotalsMutation.isPending || generateReportMutation.isPending}
              disabled={autoDrawPendingReview}
              title={autoDrawPendingReview ? 'Review auto-drawn measurements first' : undefined}
              onClick={async () => {
                if (currentMeasurementId && roofFeatures.length > 0) {
                  try {
                    // Sync linear totals to DetailedMeasurement
                    const result = await syncLinearTotalsMutation.mutateAsync({
                      measurementId: currentMeasurementId,
                      pitch: selectedPitch,
                      wasteFactor,
                    });
                    // Generate PDF report and save to opportunity/job docs
                    if (result?.id) {
                      try {
                        await generateReportMutation.mutateAsync(result.id);
                      } catch (reportError) {
                        console.error('Report generation failed (non-fatal):', reportError);
                      }
                    }
                  } catch (error) {
                    console.error('Failed to sync linear totals:', error);
                  }
                }
                navigate(`${currentMeasurementId}`);
              }}
            >
              Save & Continue
            </Button>
          </Box>
        )}
      </Stack>

      {showAdvancedMode ? (
        // Advanced Drawing Mode with two-column layout
        <>
        {autoDrawRun && ['queued', 'processing', 'completed', 'failed'].includes(autoDrawRun.status) && (
          <AutoDrawReviewBanner
            run={autoDrawRun}
            onAccept={handleAutoDrawAccept}
            onReject={handleAutoDrawReject}
            onRetry={() => handleAutoDraw(true)}
            accepting={acceptAutoDrawMutation.isPending}
            rejecting={rejectAutoDrawMutation.isPending}
          />
        )}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Left Column: Map */}
          <Grid xs={12} md={8.5}>
            <Card variant="outlined">
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <DrawableGoogleMap
                  selectedLocation={selectedLocation || undefined}
                  height={typeof window !== 'undefined' && window.matchMedia('(max-width: 959.95px)').matches ? 400 : 650}
                  roofSections={roofSections}
                  onSectionCreate={handleRoofSectionCreate}
                  onSectionUpdate={handleRoofSectionUpdate}
                  onSectionDelete={handleRoofSectionDelete}
                  roofFeatures={roofFeatures}
                  onFeatureCreate={handleFeatureCreate}
                  onFeatureUpdate={handleFeatureUpdate}
                  onFeatureDelete={handleFeatureDelete}
                  measurementId={currentMeasurementId || undefined}
                  visibleLineTypes={visibleLineTypes}
                  onVisibleLineTypesChange={setVisibleLineTypes}
                  highlightedFeatureId={highlightedFeatureId}
                  reviewPending={autoDrawPendingReview}
                  autoOffset={autoDrawPendingReview ? autoOffset : undefined}
                />
              </CardContent>
            </Card>
            {selectedLocation && (
              <CardinalViewsPanel location={selectedLocation} />
            )}
          </Grid>

          {/* Right Column: Panels */}
          <Grid xs={12} md={3.5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Roof Sections & Settings */}
              <Card variant="outlined" size="sm">
                <Typography level="title-sm" sx={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.5px', color: 'warning.500' }}>
                  Roof Sections
                </Typography>
                {roofSections.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    {(roofSections || []).map((section) => (
                      <Box key={section.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: section.color }} />
                          <Typography level="body-xs">{section.name}</Typography>
                        </Box>
                        <Typography level="body-xs" sx={{ fontWeight: 'bold' }}>
                          {Number(section.area_sqft || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} sf
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                      Draw a polygon to create a roof section — or let AI detect it.
                    </Typography>
                    <Button
                      size="sm"
                      variant="soft"
                      color="primary"
                      fullWidth
                      startDecorator={<Sparkle size={14} />}
                      disabled
                      title="AI roof detection is almost ready"
                    >
                      Auto-Draw Coming Soon!
                    </Button>
                  </Box>
                )}

                {/* Pitch & Waste Controls */}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="sm" sx={{ flex: 1 }}>
                    <FormLabel sx={{ fontSize: '10px' }}>Pitch</FormLabel>
                    <Select
                      size="sm"
                      value={selectedPitch}
                      onChange={(_, v) => v && setSelectedPitch(v)}
                      sx={{ fontSize: '12px' }}
                    >
                      {PITCH_OPTIONS.map((p) => (
                        <Option key={p} value={p}>
                          {p} (×{PITCH_MULTIPLIERS[p]})
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="sm" sx={{ flex: 1 }}>
                    <FormLabel sx={{ fontSize: '10px' }}>Waste %</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      value={wasteFactor}
                      onChange={(e) => setWasteFactor(Number(e.target.value) || 0)}
                      endDecorator="%"
                      slotProps={{ input: { min: 0, max: 50, step: 1 } }}
                      sx={{ fontSize: '12px' }}
                    />
                  </FormControl>
                </Box>
              </Card>

              {/* Linear Totals */}
              <LinearTotalsPanel
                features={roofFeatures}
                pitch={selectedPitch}
                wasteFactor={wasteFactor}
                flatArea={roofSections.reduce((sum, s) => sum + Number(s.area_sqft || 0), 0)}
                perimeterFt={roofSections.reduce((sum, s) => sum + Number(s.perimeter_ft || 0), 0)}
              />

              {/* Line Segments */}
              <Card variant="outlined" size="sm">
                <LineSegmentsPanel
                  features={roofFeatures}
                  visibleTypes={visibleLineTypes}
                  onToggleType={handleToggleLineType}
                  onDeleteFeature={handleFeatureDelete}
                  onHoverFeature={setHighlightedFeatureId}
                />
              </Card>
            </Box>
          </Grid>
        </Grid>
        </>
      ) : showCreateForm ? (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography level="h4">
                {editingMeasurement ? 'Edit Measurement' : 'Create New Measurement'}
              </Typography>
              <Button
                variant="plain"
                color="neutral"
                onClick={handleCancel}
                startDecorator={<X size={18} />}
              >
                Cancel
              </Button>
            </Box>

            <Grid container spacing={3}>
              {/* Left Column - Form Controls */}
              <Grid xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <AddressSearch
                    onAddressSelect={handleAddressSelect}
                    initialValue={selectedAddress}
                  />

                  <OpportunitySelection
                    selectedOpportunityId={formData.opportunity}
                    onOpportunitySelect={handleOpportunitySelect}
                    optional={true}
                  />

                  <JobSelection
                    selectedJobId={formData.job}
                    onJobSelect={handleJobSelect}
                  />

                  <Box>
                    <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Notes (Optional)
                    </Typography>
                    <Textarea
                      placeholder="Add any notes about this measurement..."
                      value={formData.notes || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      minRows={3}
                    />
                  </Box>

                  <Divider />

                  <Button
                    variant="solid"
                    color="primary"
                    startDecorator={<Save size={18} />}
                    onClick={handleSave}
                    disabled={!isFormValid || isSaving}
                    loading={isSaving}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {editingMeasurement ? 'Update Measurement' : 'Create Measurement'}
                  </Button>
                </Box>
              </Grid>

              {/* Right Column - Map (only show after address is selected) */}
              <Grid xs={12} md={8}>
                <Box>
                  <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Property Location
                  </Typography>
                  
                  {selectedLocation ? (
                    <>
                      <SimpleGoogleMap
                        key="measurements-map"
                        selectedLocation={selectedLocation}
                        onLocationSelect={handleLocationSelect}
                        height={typeof window !== 'undefined' && window.matchMedia('(max-width: 959.95px)').matches ? 350 : 500}
                      />
                      <Alert color="success" sx={{ mt: 2 }}>
                        Location selected: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                      </Alert>
                    </>
                  ) : (
                    <Box
                      sx={{
                        height: { xs: 250, md: 500 },
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 'sm',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'background.level1'
                      }}
                    >
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <Typography level="h4" sx={{ mb: 1 }}>
                          📍 No Location Selected
                        </Typography>
                        <Typography level="body-md">
                          Search for a property address to view the satellite map
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <>
          <MeasurementsList
            onEditMeasurement={handleStartEdit}
            onViewMeasurement={(measurement) => {
              navigate(`${measurement.id}`);
            }}
          />
        </>
      )}
    </Box>
  );
};

export const MeasurementsPage: React.FC = () => {
  return (
    <FeatureGate
      requiredTiers={['premium', 'enterprise']}
      featureName="Advanced Measurements"
      featureDescription="Professional roof measurement tools with satellite imagery, precise calculations, and integration with EagleView for accurate estimates."
      benefits={[
        'Satellite imagery and aerial measurements',
        'Interactive drawing and editing tools',
        'Precise area and perimeter calculations',
        'Multiple roof section management',
        'EagleView integration for professional reports',
        'Automatic material calculations',
        'Export to estimates and invoices',
        'Historical measurement tracking'
      ]}
    >
      <MeasurementsPageContent />
    </FeatureGate>
  );
};